import { Component, input, OnDestroy, OnInit } from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { AsyncSubject, takeUntil } from 'rxjs';
import { ProofRequirementModel } from 'src/lib/services/api/users/update-request/users-update-request.model';
import { FileUploaderComponent } from 'src/lib/templates/controls/file-uploader/file-uploader.component';
import { ModalMoverComponent } from 'src/lib/templates/global/modal-mover/modal-mover.component';
import { GroupValidationDisplayComponent } from 'src/lib/templates/layout/group-validation/group-validation-display.component';
import { GroupValidationDirective } from 'src/lib/templates/layout/group-validation/group-validation.directive';
import { SpinWhileDirective } from 'src/lib/templates/layout/spin-while/spin-while.directive';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { defaultAllowedFileTypes } from 'src/lib/utilities/file';

interface UploadProofFormGroup {
	ids: string[];
	file: File;
}

class UserUpdateField {
	id: string;
	title: string;
	description: string;
}

@Component({
	selector: 'ae-upload-proof-modal',
	templateUrl: './upload-proof-modal.component.html',
	styleUrls: ['./upload-proof-modal.component.scss'],
	imports: [
		FileUploaderComponent,
		FormsModule,
		GroupValidationDirective,
		GroupValidationDisplayComponent,
		ModalMoverComponent,
		NgSelectModule,
		ReactiveFormsModule,
		SpinWhileDirective,
	],
})
export class UploadProofModalComponent implements OnInit, OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();

	public proofMap = input.required<Map<string, ProofRequirementModel>>();

	protected loading = true;
	private selectedValues: string[] = [];
	protected originalFields: UserUpdateField[];
	protected fields: UserUpdateField[] = [];

	protected proofs: FormArray<
		FormGroup<FormControlWrapper<UploadProofFormGroup>>
	>;

	//filter out slideshow extensions and spreadsheet extensions since they are not supported for upload in this modal
	private spreadsheetSlideshowExtensions = ['xls', 'ods', 'ppt', 'pps', 'odp'];
	protected allowedExtensions = defaultAllowedFileTypes.filter(
		(f) => !this.spreadsheetSlideshowExtensions.includes(f),
	);

	constructor(
		public activeModal: NgbActiveModal,
		private fb: FormBuilder,
	) {}

	ngOnInit(): void {
		if (this.proofMap() != null) {
			this.proofMap().forEach((val) => {
				this.fields.push({
					id: val.file_key,
					title: val.title,
					description: val.description,
				});
			});

			this.originalFields = this.fields;

			this.proofs = this.fb.array<
				FormGroup<FormControlWrapper<UploadProofFormGroup>>
			>([]);

			this.proofs.valueChanges
				.pipe(takeUntil(this._unsubscribe$))
				.subscribe(() => {
					const arr = [];
					this.proofs.value.forEach((p) => {
						p.ids.forEach((id) => {
							if (!arr.includes(id)) {
								arr.push(id);
							}
						});
					});

					//filter objects array to prevent duplicates in other form groups
					this.fields = this.originalFields.filter((f) => {
						return !arr.includes(f.id);
					});

					this.selectedValues = arr;
				});

			this.addProofControl();
		}
	}

	protected addProofControl = () => {
		this.loading = true;
		if (!this.allFieldsSelected()) {
			const proofFG = this.fb.group<FormControlWrapper<UploadProofFormGroup>>({
				ids: new FormControl([], Validators.required),
				file: new FormControl(null, Validators.required),
			});

			this.proofs.push(proofFG);
		}
		this.loading = false;
	};

	protected hasProof = (id: string) => {
		return this.selectedValues.includes(id);
	};

	protected allFieldsSelected = () => {
		// Get all available fields and check if they are all in dropdown boxes
		return this.selectedValues.length === this.originalFields.length;
	};

	protected hasLastValue = () => {
		const len = this.proofs?.length;
		if (this.proofs == null || len === 0) {
			return false;
		}
		return this.proofs?.at(len - 1).value.ids?.length > 0;
	};

	protected removeCtrl = (index: number) => {
		if (this.proofs?.length > 1) {
			this.proofs.removeAt(index);
			this.proofs.updateValueAndValidity();
		}
	};

	protected upload = () => {
		if (this.proofs?.valid) {
			const val = this.proofs.value;
			//send along value
			this.activeModal.close(val);
		}
	};

	protected close = (result?: boolean) => {
		if (result) {
			this.activeModal.close(result);
		} else {
			this.activeModal.dismiss();
		}
	};

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
